import { ConvergeEnv, DEFAULT_CONVERGE_ENVIRONMENT } from '@converge/data';
import { FeatureFlagId, FeatureFlags } from './feature-flags';

import { asCase } from '@converge/utils';

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Record<string, string>;
  }
}

// Populated by public/config.js before the bundle runs. Empty in local dev so
// process.env fallbacks below remain active with no behaviour change.
const rc: Record<string, string> =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ ?? {} : {};

const MOCK_COMMIT_HASH = 'localD3v';

export class Config {
  debug = process.env['REACT_APP_DEBUG'] === 'true' || false;
  apiUrl = rc['apiUrl'] ?? process.env['REACT_APP_API_URL'];
  publicUrl = process.env['PUBLIC_URL'];
  muiKey = process.env['REACT_APP_MUI_KEY'] ?? '';

  appVersion =
    process.env['REACT_APP_VERSION'] ?? MOCK_COMMIT_HASH ?? '0.0.0-dev.0';
  commitHash = process.env['REACT_APP_COMMIT_HASH'] ?? MOCK_COMMIT_HASH ?? 'x';

  pollIntervalsInSeconds = {
    // statuses: 10,
    // underwritingRules: 10,
    // submissions: 15,
    quotes: 15,
  };

  get appVersionIsShortCommitHash() {
    return this.appVersion.startsWith(this.commitHash);
  }

  get appVersionWithLeadingV() {
    if (this.appVersionIsShortCommitHash) {
      return this.appVersion;
    }

    if (this.appVersion.startsWith('v')) {
      return this.appVersion;
    }

    return `v${this.appVersion}` as const;
  }

  get appVersionWithCommitHash() {
    if (this.appVersionIsShortCommitHash) {
      return this.appVersion;
    }

    return `${this.appVersionWithLeadingV} (${this.commitHash})`;
  }

  /**
   * This is a list of issues that we have identified in the codebase that we
   * are not able to fix at this time. These booleans enable temporary solutions
   * until we can fix these issues. These booleans should be removed once the
   * issues are fixed.
   */
  readonly issues = {
    // Issue: pollingLongRunningGetSubmissionsQueryRaceConditions
    //  Description:
    //    When a user is polling the list of submissions every X seconds (which takes 4-6 seconds to
    //    respond), but then they click the "unassign" button, the UI will show the submission
    //    as unassigned, but then the polling request will come back and show the submission as
    //    assigned. This is because the polling is still running in the background.
    //  Temporary Solution:
    //    When the user clicks the "unassign" button, we will stop any polling requests mid-flight.
    //  Permanent Solution:
    //    When we switch to subscriptions for getting submissions, we can remove this.
    //    https://converge.atlassian.net/browse/CON-2967
    pollingLongRunningGetSubmissionsQueryRaceConditions: true,
  };

  slowResponseThreshold: number = process.env[
    'REACT_APP_SLOW_RESPONSE_THRESHOLD'
  ]
    ? parseInt(process.env['REACT_APP_SLOW_RESPONSE_THRESHOLD'], 10)
    : 1500; // TODO: Should be 1s or less

  readonly errorsToIgnore: (string | RegExp)[] = [
    /ResizeObserver loop limit exceeded/,
    /NotAuthorizedException/,
  ];

  convergeEnv: ConvergeEnv =
    (rc['convergeEnv'] as ConvergeEnv) ??
    (process.env['REACT_APP_CONVERGE_ENV'] as ConvergeEnv) ??
    DEFAULT_CONVERGE_ENVIRONMENT;

  get appTitle() {
    const appTitle = ['Converge'];

    if (this.convergeEnv !== ConvergeEnv.Production) {
      appTitle.push(asCase(this.convergeEnv, 'sentence'));
    }

    appTitle.push('Platform');

    appTitle.push(this.appVersion);

    return appTitle.join(' ');
  }

  logo = this.getPublicFilePath('logo.png');
  newLogo = this.getPublicFilePath('new-black-logo.svg');
  editIcon = this.getPublicFilePath('edit-icon.svg');

  graphqlUrl = this.getApiEndpointUrl('graphql');
  checkBox = this.getPublicFilePath('check-box-selected.svg');
  unselectedCheckBox = this.getPublicFilePath('check-box-unselected.svg');
  disabledCheckBox = this.getPublicFilePath('disabled-checkbox.svg');
  cellPhoneIcon = this.getPublicFilePath('phone-icon.svg');
  refreshTokenUrl = this.getApiEndpointUrl('refresh-token');
  cloudIcon = this.getPublicFilePath('cloud.svg');
  dnb = this.getPublicFilePath('dnb.svg');
  agentSync = this.getPublicFilePath('agent-sync.svg');
  refreshAccessTokenEveryMinutes = 5;

  userIdlesAfterMinutes = this.convergeEnv === ConvergeEnv.Production ? 60 : 60;

  get userIdlesAfterSeconds() {
    return this.userIdlesAfterMinutes * 60;
  }

  get userIdlesAfterMilliseconds() {
    return this.userIdlesAfterSeconds * 1000;
  }

  resetUserSecondsBeforeExpiry = 10;

  riskyHazardGroup = 6;

  // TODO: make me 'null'
  nullValue = -1;

  defaults = {
    waitingPeriodHours: 12,
    restorationPeriodDays: 180,
    waitingPeriodDays: 7,
    enterpriseRatingMaxFusionScoreModifier: 0.95,
    enterpriseRatingMinFusionScoreModifier: 1.2,
    enterpriseRatingFusionScoreModifier: 1,
    significantTermsConditionsMinModifier: 0.85,
    significantTermsConditionsMaxModifier: 1.15,
    significantTermsConditionsModifier: 1,
    scheduleRatingPercentage: 0,
  };

  maxFileSizeInMegabytes = 100;
  maxFileSizeInBytes = this.maxFileSizeInMegabytes * 1000000;
  maxFilesPerUpload = 10;

  getWebsocketUrl() {
    return this.getApiEndpointUrl('rater').replace(/^http/, 'ws');
  }

  paginatedTableRowsPerPageOptions: number[] = [10, 25, 50, 100];

  nodeEnv = process.env['NODE_ENV'];

  get isNodeEnvDevelopment() {
    return this.nodeEnv === 'development';
  }

  get isNodeEnvProduction() {
    return this.nodeEnv === 'production';
  }

  get isNodeEnvTest() {
    return this.nodeEnv === 'test';
  }

  get isInJest() {
    return typeof process.env['JEST_WORKER_ID'] !== 'undefined';
  }

  get enableSentry() {
    const trueString = `${true}`;
    const envVar = process.env['REACT_APP_ENABLE_SENTRY']
      ?.toLowerCase()
      .replace(/^(t|1)$/, trueString);
    return envVar === trueString || !this.isNodeEnvDevelopment;
  }

  get enableDataDog() {
    if (this.isNodeEnvDevelopment) {
      return false;
    }
    if (!this.dataDogSite) {
      return false;
    }
    if (!this.dataDogApplicationId) {
      return false;
    }
    if (!this.dataDogClientToken) {
      return false;
    }
    return true;
  }

  readonly dataDogSite = process.env['REACT_APP_DATADOG_SITE'];

  readonly dataDogApplicationId =
    process.env['REACT_APP_DATADOG_APPLICATION_ID'];

  readonly dataDogClientToken = process.env['REACT_APP_DATADOG_CLIENT_TOKEN'];

  readonly dataDogService =
    process.env['REACT_APP_DATADOG_SERVICE_NAME'] ?? 'converge-frontend';

  get isNotProduction() {
    return this.convergeEnv !== ConvergeEnv.Production;
  }

  get showEnvironmentIndicator() {
    return this.isNotProduction && !this.featureFlags.hideEnvironmentIndicator;
  }

  get enableMarkerIo() {
    return (
      !!this.markerIoProjectId &&
      [ConvergeEnv.Staging, ConvergeEnv.Production].includes(this.convergeEnv)
    );
  }

  get markerIoProjectId() {
    return process.env['REACT_APP_MARKER_IO_PROJECT_ID'] ?? '';
  }

  getApiUrl() {
    return `${this.apiUrl}`;
  }

  getApiEndpointUrl(path: string) {
    return `${this.apiUrl}/${path}`;
  }

  getPublicFilePath(path: string) {
    return `${this.publicUrl}/${path}`;
  }

  get featureFlags() {
    return new FeatureFlags();
  }

  get enableFeatureFlags() {
    return this.convergeEnv !== ConvergeEnv.Production;
  }
}

export const config = new Config();

export { FeatureFlagId };
